import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { AdminAuthContext } from '../context/AdminAuthContext';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { Menu, X, PieChart, BarChart, FileText, MessageCircle, Bell, ReceiptEuro, User, AlertCircle, TrendingUp } from 'lucide-react';
import '../page/Workspace.css';
import flogo from "../assets/images/flogo.png"
import flogowhite from "../assets/images/flogowhite.png";
import API_BASE_URL from '../config';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout: logoutUser } = useContext(AuthContext);
  const { admin, logout: logoutAdmin } = useContext(AdminAuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [paymentConfigured, setPaymentConfigured] = useState(true);
  const { theme, toggleTheme } = useTheme();
  const [businessUnreadCount, setBusinessUnreadCount] = useState(0);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        document.querySelector('[aria-controls="workspace-navigation"]')?.focus();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isMobileMenuOpen]);

  // New function to close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const [logoutError, setLogoutError] = useState(false);
  const logout = async () => {
    setLogoutError(false);
    try {
    if (admin) {
      await logoutAdmin();
    } else {
      await logoutUser();
    }
    // Close mobile menu when logging out
    closeMobileMenu();
    } catch { setLogoutError(true); }
  };

  const updateUnreadCount = useCallback(async () => {
    if (user) {
      try {
        const response = await axios.get(`${API_BASE_URL}/threads/unread-count/${encodeURIComponent(user)}`);
        setUnreadCount(response.data.unreadCount);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    }
  }, [user]);

  const updateBusinessUnreadCount = useCallback(async () => {
    if (admin) {
      try {
        const response = await axios.get(`${API_BASE_URL}/business/unread-count`);
        setBusinessUnreadCount(response.data.unreadCount || 0);
      } catch (error) {
        console.error('Error fetching business unread count:', error);
      }
    }
  }, [admin]);

  useEffect(() => {
    if (window) {
      window.updateNavbarUnreadCount = updateUnreadCount;
      window.updateBusinessUnreadCount = updateBusinessUnreadCount;
    }
    updateUnreadCount();
    updateBusinessUnreadCount();
    return () => {
      if (window) {
        delete window.updateNavbarUnreadCount;
        delete window.updateBusinessUnreadCount;
      }
    };
  }, [updateUnreadCount, updateBusinessUnreadCount]);


  useEffect(() => {
    const controller = new AbortController();
    setProfileImage(null);
    setPaymentConfigured(true);
    if (user) {
      axios.get(`${API_BASE_URL}/auth/profile?username=${encodeURIComponent(user)}`, { signal: controller.signal })
        .then((response) => {
          if (controller.signal.aborted) return;
          const userData = response.data.user;
          setProfileImage(
            userData.profileImage
              ? `${API_BASE_URL}/${userData.profileImage}`
              : null
          );

          // Check if payment method is configured
          const method = userData.paymentMethod;
          let hasAddress = false;

          if (method === 'paypal' && userData.paypalAddress) {
            hasAddress = true;
          } else if (method === 'bitcoin' && userData.bitcoinAddress) {
            hasAddress = true;
          } else if (method === 'ETH' && userData.ethAddress) {
            hasAddress = true;
          } else if (method === 'SOL' && userData.solAddress) {
            hasAddress = true;
          } else if (method === 'BNB' && userData.bnbAddress) {
            hasAddress = true;
          } else if (method === 'POL' && userData.polAddress) {
            hasAddress = true;
          } else if (method === 'LTC' && userData.ltcAddress) {
            hasAddress = true;
          } else if (method === 'DOGE' && userData.dogeAddress) {
            hasAddress = true;
          } else if (method === 'SHIB' && userData.shibAddress) {
            hasAddress = true;
          }

          setPaymentConfigured(hasAddress);
        })
        .catch((error) => {
          console.error('Error fetching profile:', error);
        });
    }
    return () => controller.abort();
  }, [user]);

  // Create a custom NavLink component that closes the mobile menu when clicked
  const NavLink = ({ to, children, className }) => {
    const { i18n } = useTranslation();
    const currentLang = location.pathname.startsWith('/it/') ? 'it' : location.pathname.startsWith('/en/') ? 'en' : i18n.language?.startsWith('it') ? 'it' : 'en';

    // Check if it's an admin path - if so, don't add language prefix
    const isAdminPath = to.startsWith('/admin');
    const finalPath = isAdminPath ? to : `/${currentLang}${to}`;

    return (
      <RouterNavLink
        end={to === '/admin'}
        to={finalPath}
        className={className}
        onClick={closeMobileMenu}
      >
        {children}
      </RouterNavLink>
    );
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="fa-mobile-nav lg:hidden fixed top-0 left-0 right-0 bg-white dark:bg-dark-bg dark:border-b dark:border-dark-accent shadow-sm p-4 flex items-center justify-between z-50">
        <NavLink to={admin ? "/admin" : "/dashboard"} className="flex items-center gap-2">
          <img
            src={theme === 'dark' ? flogowhite : flogo}
            alt="Fast Affiliation"
            className="h-8"
          />
          <span className="fa-sidebar-brand">FastAffiliation<span>.</span></span>
        </NavLink>
        <button
          aria-label={isMobileMenuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="workspace-navigation"
          onClick={toggleMobileMenu}
          className="text-gray-600 dark:text-dark-text p-2 hover:bg-light-green dark:hover:bg-dark-accent rounded-lg"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && <button className="fa-sidebar-backdrop" aria-label="Close navigation backdrop" onClick={closeMobileMenu} />}
      {/* Sidebar Navigation */}
      <div id="workspace-navigation" className={`fa-sidebar fixed top-0 left-0 w-64 bg-white dark:bg-dark-bg border-r border-gray-200 dark:border-dark-accent flex flex-col h-screen transition-transform duration-300 ease-in-out z-40
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>

        {/* Logo */}
        <div className="p-6 hidden lg:block">
          <NavLink to={admin ? "/admin" : "/dashboard"} className="flex items-center gap-2">
            <img
              src={theme === 'dark' ? flogowhite : flogo}
              alt="Fast Affiliation"
              className="h-8"
            />
            <span className="font-bold text-[#1F2421] dark:text-dark-text">{t('navbar.brand')}</span>
          </NavLink>
        </div>

        {/* Mobile spacing */}
        <div className="lg:hidden h-16"></div>

        {/* User info at top */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-dark-accent border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-400 dark:text-gray-500 mb-1">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M3 22C3 17.0294 7.02944 13 12 13C16.9706 13 21 17.0294 21 22" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('navbar.uploadPhoto')}</span>
                </>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-400">{admin ? 'Admin' : t('navbar.affiliate')}</p>
              <p className="fa-sidebar-identity font-medium">{user || admin || t('navbar.guest')}</p>
            </div>
          </div>
        </div>

        {/* Payment Method Warning */}
        {user && !admin && !paymentConfigured && (
          <div className="mx-6 mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex gap-2">
              <AlertCircle size={18} className="text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-800 dark:text-yellow-400 font-medium">
                  {t('navbar.paymentMethodMissing', 'Payment method not configured')}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                  {t('navbar.configurePaymentMethod', 'Please configure your payment method to receive earnings.')}
                </p>
                <NavLink
                  to="/payments"
                  className="mt-2 text-xs font-medium flex items-center gap-1 text-yellow-700 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300"
                >
                  {t('navbar.goToPaymentSettings', 'Configure now')}
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </NavLink>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 px-6 py-4">
          {/* Regular user links */}
          {user && !admin && (
            <>
              <NavLink to={admin ? "/admin" : "/dashboard"} className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <PieChart size={20} />
                <span>{t('navbar.dashboard')}</span>
              </NavLink>
              <NavLink to="/conversionlist" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <BarChart size={20} />
                <span>{t('navbar.conversions')}</span>
              </NavLink>
              <NavLink to="/campaignlist" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <FileText size={20} />
                <span>{t('navbar.campaigns')}</span>
              </NavLink>
              <NavLink to="/messages" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <div className="relative">
                  <MessageCircle size={20} />
                  {unreadCount > 0 && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                  )}
                </div>
                <span>{t('navbar.messages')}</span>
              </NavLink>
              <NavLink to="/announcements" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <Bell size={20} />
                <span>{t('navbar.announcements')}</span>
              </NavLink>
              <NavLink to="/payments" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <div className="relative">
                  <ReceiptEuro size={20} />
                  {!paymentConfigured && (
                    <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      !
                    </div>
                  )}
                </div>
                <span>{t('navbar.payments')}</span>
              </NavLink>
            </>
          )}

          {/* Admin links */}
          {admin && (
            <>
              <NavLink to="/admin" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <PieChart size={20} />
                <span>{t('navbar.admin')}</span>
              </NavLink>
              <NavLink to="/admin/manage" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <FileText size={20} />
                <span>{t('navbar.manage')}</span>
              </NavLink>
              <NavLink to="/admin/allconversion" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <BarChart size={20} />
                <span>{t('navbar.allConversions')}</span>
              </NavLink>
              <NavLink to="/admin/messages" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <MessageCircle size={20} />
                <span>{t('navbar.messages')}</span>
              </NavLink>
              <NavLink to="/admin/announcements" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <Bell size={20} />
                <span>{t('navbar.announcements')}</span>
              </NavLink>
              <NavLink to="/admin/newsletter" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <Bell size={20} />
                <span>{t('navbar.newsletter')}</span>
              </NavLink>
              <NavLink to="/admin/contact" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <Bell size={20} />
                <span>{t('navbar.contacts')}</span>
              </NavLink>
              <NavLink to="/admin/payments" className="flex items-center gap-3 px-3 py-2 rounded-lg mb-1"><ReceiptEuro size={20} /><span>{t('navbar.payments')}</span></NavLink>
              <NavLink to="/admin/business" className="flex items-center gap-3 px-3 py-2 rounded-lg mb-1"><MessageCircle size={20} /><span>Business {businessUnreadCount > 0 ? '(' + businessUnreadCount + ')' : ''}</span></NavLink>
              <NavLink to="/admin/analytics" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
                <TrendingUp size={20} />
                <span>Analytics</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* Bottom links */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-accent">
          {!admin && <NavLink to="/profile" className="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1">
            <User size={20} />
            <span>{t('navbar.profile')}</span>
          </NavLink>}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-dark-text hover:bg-light-green dark:hover:bg-dark-accent rounded-lg mb-1"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            <span>{t('navbar.theme')}</span>
          </button>
          {logoutError && <p role="alert" className="text-sm text-red-300">Logout non riuscito. Riprova.</p>}
          {(user || admin) ? (
            <button
              onClick={logout}
              className="w-full mt-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t('navbar.logout')}
            </button>
          ) : (
            <NavLink
              to="/login"
              className="w-full mt-4 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-4 py-2 rounded-lg text-sm font-medium text-center"
            >
              {t('navbar.login')}
            </NavLink>
          )}
        </div>
      </div>
    </>
  );
}

export default Navbar;
